/**
 * <s-product-gallery> — one-at-a-time product media carousel.
 * The track is a native scroll-snap row; this only wires the arrows,
 * the counter and the desktop thumbnails to it.
 *
 * Markup: [data-gallery-track] > [data-gallery-slide]*, [data-gallery-prev],
 * [data-gallery-next], [data-gallery-current], [data-gallery-thumb="i"].
 */
class SProductGallery extends HTMLElement {
  #ac;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-gallery-slide]')];

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) return this.goTo(Number(thumb.dataset.galleryThumb));
      if (event.target.closest('[data-gallery-prev]')) return this.goTo(this.#index - 1);
      if (event.target.closest('[data-gallery-next]')) return this.goTo(this.#index + 1);
    }, opts);

    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.#sync());
    }, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.raf);
  }

  goTo(index) {
    const count = this.slides.length;
    if (!count) return;
    const next = (index + count) % count;
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    const start = next * this.track.clientWidth * direction;
    this.track.scrollTo({ left: start, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    this.#setIndex(next);
  }

  #sync() {
    const width = this.track.clientWidth || 1;
    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    this.#setIndex(Math.min(Math.max(index, 0), this.slides.length - 1));
  }

  #setIndex(index) {
    this.#index = index;
    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(index + 1);
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.galleryThumb) === index));
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
